'use client';

import { useMemo, useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useCart } from '@/hooks/useCart';
import { Button } from '@/components/ui/button';
import { Minus, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';

export function ProductDrawer({ product, isOpen, onClose, isCartBlocked = false, category = '' }) {
  const { cart, addToCart, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();
  const { user } = useUser();
  const [customWeight, setCustomWeight] = useState('');
  const [customWeightGrams, setCustomWeightGrams] = useState(0);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  const cartItems = useMemo(() => {
    if (!product) return [];
    return cart.filter((item) => item.id === product.productId);
  }, [cart, product]);

  // Find custom weight item in cart
  const customWeightItem = useMemo(() => {
    return cartItems.find((item) => item.variant.isCustomWeight);
  }, [cartItems]);

  const calculateCartTotal = useMemo(() => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [cartItems]);

  const handleQuantityChange = (variant, change) => {
    console.log(variant, change);

    if (change > 0) {
      // Add the variant to cart
      addToCart({
        id: product.productId,
        name: product.productName,
        image: product.productImageUrl,
        mrp: variant.mrp || product.mrp,
        price: variant.netPrice || product.netPrice,
        unit: product?.baseUnit,
        quantity: 1,
        variant: variant,
      });
    } else {
      updateQuantity(product.productId, variant.inventoryId, change);
    }
  };

  const handleCustomWeightChange = (e) => {
    const value = e.target.value;
    setCustomWeight(value);

    // Extract numeric value from input (remove non-numeric characters)
    const numericValue = value.replace(/[^0-9]/g, '');

    // Convert to number
    const grams = numericValue ? Number.parseInt(numericValue, 10) : 0;
    setCustomWeightGrams(grams);

    // Clear any existing timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Set a new timeout to update the cart after typing stops
    const timeout = setTimeout(() => {
      updateCustomWeight(grams);
    }, 500); // 500ms debounce

    setDebounceTimeout(timeout);
  };

  const updateCustomWeight = (grams) => {
    // If grams is 0 or invalid, remove the custom weight item if it exists
    if (grams <= 0) {
      if (customWeightItem) {
        removeFromCart(product.productId, customWeightItem.variant.inventoryId);
      }
      return;
    }

    // Calculate price based on grams (convert to kg for calculation)
    const weightInKg = grams / 1000;
    const itemPrice = product.netPrice * weightInKg;

    // If there's already a custom weight item, remove it first
    if (customWeightItem) {
      removeFromCart(product.productId, customWeightItem.variant.inventoryId);
    }

    // Add new custom weight item
    const customVariant = {
      inventoryId: 'custom-' + Date.now(), // Generate unique ID
      variant: grams.toString(),
      unit: 'gm',
      netPrice: itemPrice,
      mrp: product.mrp * weightInKg,
      isCustomWeight: true,
      weightInGrams: grams,
      pricePerKg: product.netPrice,
    };

    addToCart({
      id: product.productId,
      name: product.productName,
      image: product.productImageUrl,
      mrp: product.mrp * weightInKg,
      unit: product?.baseUnit,
      price: itemPrice,
      quantity: 1,
      variant: customVariant,
    });
  };

  const handleCustomWeightQuantityChange = (change) => {
    if (!customWeightItem) return;

    // For custom weight, we only allow a quantity of 1
    if (change < 0) {
      handleRemoveCustomWeight();
    }
    // We don't allow increasing custom weight quantity beyond 1
  };

  const handleRemoveCustomWeight = () => {
    if (customWeightItem) {
      removeFromCart(product.productId, customWeightItem.variant.inventoryId);
      setCustomWeight('');
      setCustomWeightGrams(0);
    }
  };

  // Update input field when custom weight item changes
  useEffect(() => {
    if (customWeightItem) {
      setCustomWeight(customWeightItem.variant.weightInGrams.toString());
      setCustomWeightGrams(customWeightItem.variant.weightInGrams);
    }
  }, [customWeightItem]);

  const getItemQuantity = (inventoryId) => {
    const item = cartItems.find((item) => item.variant.inventoryId === inventoryId);
    return item ? item.quantity : 0;
  };

  const handleGoToCart = () => {
    onClose();
    navigate('/cart');
  };

  if (!product) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="max-w-md mx-auto py-0 px-0 rounded-t-3xl">
        <div className="flex flex-col h-full">
          <div className="relative w-full h-40 rounded-t-2xl overflow-hidden">
            <img
              loading="lazy"
              src={product.productImageUrl || '/placeholder.svg'}
              alt={product.productName}
              className="w-full h-full object-cover"
            />
            {product.mrp > product.netPrice && (
              <div className="absolute top-2 left-2 text-white bg-[#39c55e] text-xs px-2 py-1 rounded">
                {(((product.mrp - product.netPrice) / product.mrp) * 100).toFixed(0)}% OFF
              </div>
            )}
          </div>
          <div className="flex-grow overflow-y-auto px-4 py-2">
            <SheetHeader className="text-left mb-2">
              <SheetTitle className="text-xl font-bold">{product.productName}</SheetTitle>
              {product.mrp > product.netPrice && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-gray-500 line-through">MRP: ₹{product.mrp}</span>
                  <span className="text-sm font-medium">₹{product.netPrice}</span>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                    {(((product.mrp - product.netPrice) / product.mrp) * 100).toFixed(0)}% OFF
                  </span>
                </div>
              )}
            </SheetHeader>
            <div className="grid gap-2">
              {product.productVariants.map((variant) => (
                <div
                  key={variant.inventoryId}
                  className="flex items-center justify-between border rounded-lg p-2"
                >
                  <div className="flex flex-col items-start justify-start">
                    <span className="text-sm font-medium">
                      {variant.variant} {variant.unit}
                    </span>
                    {(variant.mrp || product.mrp) > variant.netPrice ? (
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400 line-through">
                          MRP: ₹{variant.mrp || product.mrp}
                        </p>
                        <p className="text-xs font-medium">₹{variant.netPrice}</p>
                      </div>
                    ) : (
                      <p className="text-xs font-medium">₹{variant.netPrice}</p>
                    )}
                  </div>
                  {user?.userType === 'user' && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleQuantityChange(variant, -1)}
                        disabled={getItemQuantity(variant.inventoryId) <= 0}
                        className="h-7 w-7 p-0"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center">
                        {getItemQuantity(variant.inventoryId)}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleQuantityChange(variant, 1)}
                        disabled={getItemQuantity(variant.inventoryId) >= 9}
                        className="h-7 w-7 p-0"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {user?.userType === 'user' && product?.baseUnit !== 'pc' && (
                <div className="border rounded-lg p-2">
                  <div className="flex items-center justify-between border border-gray-200 pr-2 rounded-lg mb-2">
                    <input
                      type="text"
                      className="focus:outline-none border-none rounded-lg w-full pl-2 py-2"
                      placeholder="Enter custom weight (e.g., 500gm)"
                      value={customWeight}
                      onChange={handleCustomWeightChange}
                      disabled={isCartBlocked}
                    />

                    {!isCartBlocked && customWeightItem && (
                      <div className="flex items-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveCustomWeight}
                          className="h-7 px-2"
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>

                  {customWeightItem && (
                    <div className="flex items-center justify-between">
                      <div className="text-xs">
                        {customWeightItem.variant.mrp > customWeightItem.price ? (
                          <>
                            <span className="text-gray-500 line-through">
                              MRP: ₹{customWeightItem.variant.mrp.toFixed(2)}
                            </span>
                            <span className="ml-2 font-medium">
                              Price: ₹{customWeightItem.price.toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span className="font-medium">
                            Price: ₹{customWeightItem.price.toFixed(2)}
                          </span>
                        )}
                        <span className="block text-gray-500">
                          for {customWeightItem.variant.weightInGrams}gm
                        </span>
                      </div>

                      {!isCartBlocked && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRemoveCustomWeight}
                            className="h-7 px-2"
                          >
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="border-t bg-white px-4 py-3 mt-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium">Total:</span>
              <div className="text-right">
                {cartItems.some((item) => item.mrp > item.price) ? (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 line-through">
                        MRP: ₹
                        {cartItems
                          .reduce((total, item) => total + item.mrp * item.quantity, 0)
                          .toFixed(2)}
                      </span>
                      <span className="font-bold text-lg">₹{calculateCartTotal.toFixed(2)}</span>
                    </div>
                    {calculateCartTotal > 0 && (
                      <span className="text-xs text-green-600">
                        You save: ₹
                        {(
                          cartItems.reduce((total, item) => total + item.mrp * item.quantity, 0) -
                          calculateCartTotal
                        ).toFixed(2)}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="font-bold text-lg">₹{calculateCartTotal.toFixed(2)}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 cursor-pointer"
                onClick={() => onClose()}
              >
                Continue
              </Button>
              {user?.userType === 'user' && (
                <Button
                  className="w-full bg-[#39c55e] hover:bg-[#2ea34d] text-white py-4 cursor-pointer"
                  onClick={handleGoToCart}
                  disabled={calculateCartTotal === 0 || isCartBlocked}
                >
                  {!isCartBlocked ? 'Go To Cart' : 'Not Available'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
